import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface MarketingStats {
  total_subscribers: number;
  active_subscribers: number;
  unsubscribed: number;
  total_campaigns: number;
  total_emails_sent: number;
  avg_recipients_per_campaign: number;
  last_campaign_sent: string;
}

export interface Campaign {
  id: string;
  title: string;
  subject: string;
  content: string;
  recipients_count: number;
  sent_at: string;
  created_at: string;
  sent_by_name: string;
  sent_by_email: string;
}

export interface CampaignHistoryResponse {
  status: string;
  data: {
    campaigns: Campaign[];
    total: number;
    limit: number;
    offset: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Subscriber {
  id: number;
  email: string;
  user_id: number;
  first_name: string;
  last_name: string;
  created_at: string;
}

export interface SubscribersResponse {
  status: string;
  data: {
    subscribers: Subscriber[];
    total: number;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SendCampaignData {
  title: string;
  subject: string;
  content: string;
}

export interface SendCampaignResponse {
  status: string;
  message: string;
  data: {
    campaign: Campaign;
    stats: {
      totalSubscribers: number;
      successfulSends: number;
      failedSends: number;
    };
  };
}

export interface EliteListing {
  id: number;
  title: string;
  price: number;
  currency: string;
  category: string;
  subcategory: string;
  table_name: string;
  status: string;
  created_at: string;
  images: string[];
  first_image: string | null;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  plan_name: string;
}

export interface EliteListingsResponse {
  status: string;
  data: {
    listings: EliteListing[];
    total: number;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SendListingCampaignData {
  listingIds: number[];
  subject?: string;
  intro?: string;
}

export interface SendListingCampaignResponse {
  status: string;
  message: string;
  data: {
    campaign: {
      id: string;
      type: string;
      listings_count: number;
    };
    stats: {
      totalSubscribers: number;
      successfulSends: number;
      failedSends: number;
    };
  };
}

export interface MarketingPreferences {
  marketing_consent: boolean;
}

export interface MarketingPreferencesResponse {
  status: string;
  message?: string;
  data?: MarketingPreferences;
}

export interface CampaignFilters {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  campaign_type?: string;
  status?: string;
}

export interface SubscriberFilters {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MarketingService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';

  constructor(private http: HttpClient) {}

  sendCampaign(
    campaignData: SendCampaignData
  ): Observable<SendCampaignResponse> {
    return this.http
      .post<SendCampaignResponse>(
        `${this.apiUrl}/marketing/campaigns`,
        campaignData
      )
      .pipe(catchError(this.handleError));
  }

  getCampaignHistory(
    filters: CampaignFilters = {},
    page: number = 1,
    limit: number = 20
  ): Observable<CampaignHistoryResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof CampaignFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http
      .get<CampaignHistoryResponse>(`${this.apiUrl}/marketing/campaigns`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  getCampaignById(campaignId: string): Observable<Campaign> {
    return this.http
      .get<{ status: string; data: Campaign }>(
        `${this.apiUrl}/marketing/campaigns/${campaignId}`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getMarketingStats(): Observable<MarketingStats> {
    return this.http
      .get<{ status: string; data: MarketingStats }>(
        `${this.apiUrl}/marketing/stats`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  getEliteListings(
    page: number = 1,
    limit: number = 50
  ): Observable<EliteListingsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http
      .get<EliteListingsResponse>(`${this.apiUrl}/marketing/elite-listings`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  sendListingCampaign(campaignData: {
    listingIds: number[];
    subject?: string;
    intro?: string;
  }): Observable<SendListingCampaignResponse> {
    return this.http
      .post<SendListingCampaignResponse>(
        `${this.apiUrl}/marketing/campaigns/listings`,
        campaignData
      )
      .pipe(catchError(this.handleError));
  }

  getSubscribers(
    filters: SubscriberFilters = {},
    page: number = 1,
    limit: number = 100
  ): Observable<SubscribersResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof SubscriberFilters];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http
      .get<SubscribersResponse>(`${this.apiUrl}/marketing/subscribers`, {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  updateMarketingPreferences(
    marketingConsent: boolean
  ): Observable<MarketingPreferencesResponse> {
    return this.http
      .put<MarketingPreferencesResponse>(
        `${this.apiUrl}/marketing/preferences`,
        {
          marketing_consent: marketingConsent,
        }
      )
      .pipe(catchError(this.handleError));
  }

  getMarketingPreferences(): Observable<MarketingPreferences> {
    return this.http
      .get<MarketingPreferencesResponse>(`${this.apiUrl}/marketing/preferences`)
      .pipe(
        map((response) => response.data || { marketing_consent: false }),
        catchError(this.handleError)
      );
  }

  unsubscribeByToken(
    token: string
  ): Observable<{ status: string; message: string }> {
    return this.http
      .get<{ status: string; message: string }>(
        `${this.apiUrl}/marketing/unsubscribe/${token}`
      )
      .pipe(catchError(this.handleError));
  }

  deleteCampaign(campaignId: string): Observable<{ status: string; message: string }> {
    return this.http
      .delete<{ status: string; message: string }>(
        `${this.apiUrl}/marketing/campaigns/${campaignId}`
      )
      .pipe(catchError(this.handleError));
  }

  exportSubscribers(format: 'csv' | 'excel' = 'csv'): Observable<Blob> {
    const params = new HttpParams().set('format', format);

    return this.http
      .get(`${this.apiUrl}/marketing/subscribers/export`, {
        params,
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError));
  }

  getCampaignAnalytics(
    campaignId: string
  ): Observable<{
    opens: number;
    clicks: number;
    bounces: number;
    unsubscribes: number;
    delivery_rate: number;
    open_rate: number;
    click_rate: number;
  }> {
    return this.http
      .get<{ status: string; data: any }>(
        `${this.apiUrl}/marketing/campaigns/${campaignId}/analytics`
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  formatCampaignStatus(status: string): string {
    switch (status.toLowerCase()) {
      case 'sent':
        return 'Sent';
      case 'draft':
        return 'Draft';
      case 'scheduled':
        return 'Scheduled';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }

  formatCampaignType(type: string): string {
    switch (type.toLowerCase()) {
      case 'newsletter':
        return 'Newsletter';
      case 'promotional':
        return 'Promotional';
      case 'listing_showcase':
        return 'Listing Showcase';
      case 'announcement':
        return 'Announcement';
      default:
        return type;
    }
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch (error) {
      return 'Unknown date';
    }
  }

  validateCampaignData(campaignData: SendCampaignData): string[] {
    const errors: string[] = [];

    if (!campaignData.title || campaignData.title.trim().length < 3) {
      errors.push('Campaign title must be at least 3 characters long');
    }

    if (!campaignData.subject || campaignData.subject.trim().length < 3) {
      errors.push('Email subject must be at least 3 characters long');
    }

    if (!campaignData.content || campaignData.content.trim().length < 10) {
      errors.push('Email content must be at least 10 characters long');
    }

    if (campaignData.title && campaignData.title.length > 255) {
      errors.push('Campaign title cannot exceed 255 characters');
    }

    if (campaignData.subject && campaignData.subject.length > 255) {
      errors.push('Email subject cannot exceed 255 characters');
    }

    return errors;
  }

  validateListingCampaignData(campaignData: SendListingCampaignData): string[] {
    const errors: string[] = [];

    if (!campaignData.listingIds || campaignData.listingIds.length === 0) {
      errors.push('At least one listing must be selected');
    }

    if (campaignData.listingIds && campaignData.listingIds.length > 20) {
      errors.push('Maximum 20 listings can be sent in one campaign');
    }

    return errors;
  }

  getCampaignPreview(campaignData: SendCampaignData): {
    title: string;
    subject: string;
    contentPreview: string;
    estimatedReadTime: string;
  } {
    const contentText = campaignData.content.replace(/<[^>]*>/g, '').trim();
    const wordCount = contentText.split(/\s+/).length;
    const readTimeMinutes = Math.ceil(wordCount / 200);

    return {
      title: campaignData.title || 'Untitled Campaign',
      subject: campaignData.subject || 'No Subject',
      contentPreview:
        contentText.substring(0, 150) + (contentText.length > 150 ? '...' : ''),
      estimatedReadTime: `${readTimeMinutes} min read`,
    };
  }

  getListingCampaignPreview(listings: EliteListing[]): {
    title: string;
    listingsCount: number;
    categoriesCount: number;
    totalValue: number;
    averagePrice: number;
  } {
    const categories = new Set(listings.map((l) => l.category));
    const totalValue = listings.reduce(
      (sum, listing) => sum + listing.price,
      0
    );
    const averagePrice = listings.length > 0 ? totalValue / listings.length : 0;

    return {
      title: `Premium Listings Showcase - ${listings.length} Elite Items`,
      listingsCount: listings.length,
      categoriesCount: categories.size,
      totalValue,
      averagePrice,
    };
  }

  getSubscriberGrowthData(subscribers: Subscriber[]): {
    labels: string[];
    data: number[];
  } {
    const monthlyData = new Map<string, number>();

    subscribers.forEach((subscriber) => {
      const date = new Date(subscriber.created_at);
      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}`;
      monthlyData.set(monthKey, (monthlyData.get(monthKey) || 0) + 1);
    });

    const sortedEntries = Array.from(monthlyData.entries()).sort();

    return {
      labels: sortedEntries.map(([month]) => {
        const [year, monthNum] = month.split('-');
        const date = new Date(parseInt(year), parseInt(monthNum) - 1);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        });
      }),
      data: sortedEntries.map(([, count]) => count),
    };
  }

  getCampaignMetrics(campaigns: Campaign[]): {
    totalSent: number;
    totalRecipients: number;
    averageRecipientsPerCampaign: number;
    campaignsByMonth: { [key: string]: number };
  } {
    const totalSent = campaigns.length;
    const totalRecipients = campaigns.reduce(
      (sum, campaign) => sum + (campaign.recipients_count || 0),
      0
    );
    const averageRecipientsPerCampaign = totalSent > 0 ? totalRecipients / totalSent : 0;

    const campaignsByMonth: { [key: string]: number } = {};
    campaigns.forEach((campaign) => {
      const date = new Date(campaign.sent_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      campaignsByMonth[monthKey] = (campaignsByMonth[monthKey] || 0) + 1;
    });

    return {
      totalSent,
      totalRecipients,
      averageRecipientsPerCampaign,
      campaignsByMonth,
    };
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 403) {
      errorMessage = 'Access denied. Admin privileges required.';
    } else if (error.status === 401) {
      errorMessage = 'Authentication required. Please log in again.';
    } else if (error.status === 404) {
      errorMessage = 'Resource not found.';
    } else if (error.status === 429) {
      errorMessage =
        'Too many requests. Please wait before sending another campaign.';
    } else if (error.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    console.error('Marketing service error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
